import { backboneService } from '@/services/backbone.js';
import { assetsClient } from '@/clients/assets-client.js';
import { toastService } from '@/services/toast.js';
import { HatElement } from '@/components/hat-element.js';
import { assetTypesCache } from '@/services/asset-types-cache.js';
import type { Asset } from '@/clients/assets-client.js';

class HatAssetCard extends HatElement {
    private _data: Asset | null = null;
    private _assetTypeLabel: string | null = null;
    private _closeMenusHandler: (() => void) | null = null;
    private _docClickHandler: (() => void) | null = null;

    constructor() {
        super();
        this.root.innerHTML = `
            <style>
                .card {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    background: whitesmoke;
                    padding: 14px;
                    border-radius: 12px;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
                    transition: box-shadow 0.1s ease;
                }

                .card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.14); }

                .thumbnail {
                    width: 80px;
                    height: 80px;
                    flex-shrink: 0;
                    border-radius: 8px;
                    overflow: hidden;
                    background: #e0e0e0;
                    cursor: pointer;
                }

                .content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    cursor: pointer;
                }

                .name {
                    font-weight: 600;
                    font-size: 16px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .asset-type {
                    font-size: 13px;
                    color: #555;
                }

                .menu { position: relative; }

                .menu > button {
                    background: none;
                    border: none;
                    font-size: 18px;
                    cursor: pointer;
                    padding: 4px 8px;
                }

                .dropdown {
                    position: absolute;
                    right: 0;
                    top: 28px;
                    background: white;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    display: none;
                    flex-direction: column;
                    min-width: 120px;
                    overflow: hidden;
                    z-index: 10;
                }

                .dropdown button {
                    padding: 8px 12px;
                    border: none;
                    background: none;
                    text-align: left;
                    cursor: pointer;
                    font-size: 14px;
                }

                .dropdown button:hover { background: #f2f2f2; }

                .menu.open .dropdown { display: flex; }
            </style>

            <div class="card">
                <div class="thumbnail"></div>
                <div class="content">
                    <div class="name"></div>
                    <div class="asset-type"></div>
                </div>
                <div class="menu" hat-entitlement="feature.asset.edit feature.asset.delete">
                    <button>⋮</button>
                    <div class="dropdown">
                        <button data-action="edit" hat-entitlement="feature.asset.edit">Edit</button>
                        <button data-action="delete" hat-entitlement="feature.asset.delete">Delete</button>
                    </div>
                </div>
            </div>
        `;
    }

    async setData(data: Asset) {
        this._data = data;
        if (data.assetType) {
            const type = await assetTypesCache.getByCode(data.assetType);
            this._assetTypeLabel = type?.description ?? data.assetType;
        }
        this._render();
    }

    _render() {
        if (!this._data) return;

        const nameEl    = this.root.querySelector<HTMLElement>('.name')!;
        const typeEl    = this.root.querySelector<HTMLElement>('.asset-type')!;
        const thumbEl   = this.root.querySelector<HTMLElement>('.thumbnail')!;

        nameEl.textContent  = this._data.name ?? 'Unnamed asset';
        typeEl.textContent  = this._assetTypeLabel ?? this._data.assetType ?? '';

        const thumbnailDocId = (this._data as any).displayImage?.thumbnailDocId;
        if (thumbnailDocId) {
            thumbEl.innerHTML = `<hat-image type="thumbnail" document-id="${thumbnailDocId}" style="width:100%;height:100%;"></hat-image>`;
        }

        const navigate = () => backboneService.navigateTo(
            `/app/locations/${this._data!.locationId}/assets/${this._data!.id}`
        );
        thumbEl.onclick  = navigate;
        nameEl.onclick   = navigate;
        typeEl.onclick   = navigate;

        this._setupMenu();
    }

    connectedCallback() {
        super.connectedCallback();
        const menu = this.root.querySelector<HTMLElement>('.menu')!;
        this._closeMenusHandler = () => menu.classList.remove('open');
        this._docClickHandler   = () => menu.classList.remove('open');
        document.addEventListener('hat-close-menus', this._closeMenusHandler);
        document.addEventListener('click', this._docClickHandler);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._closeMenusHandler) document.removeEventListener('hat-close-menus', this._closeMenusHandler);
        if (this._docClickHandler)   document.removeEventListener('click', this._docClickHandler);
    }

    _setupMenu() {
        const menu    = this.root.querySelector<HTMLElement>('.menu')!;
        const button  = menu.querySelector('button')!;
        const dropdown = menu.querySelector('.dropdown')!;

        button.onclick = (e) => {
            e.stopPropagation();
            const isOpen = menu.classList.contains('open');
            document.dispatchEvent(new CustomEvent('hat-close-menus'));
            if (!isOpen) menu.classList.add('open');
        };

        dropdown.querySelector<HTMLElement>('[data-action="edit"]')!.onclick = (e) => {
            e.stopPropagation();
            menu.classList.remove('open');
            backboneService.navigateTo(
                `/app/locations/${this._data!.locationId}/assets/${this._data!.id}/edit`
            );
        };

        dropdown.querySelector<HTMLElement>('[data-action="delete"]')!.onclick = async (e) => {
            e.stopPropagation();
            menu.classList.remove('open');
            try {
                await assetsClient.deleteAsset(this._data!.locationId!, this._data!.id!);
                this.dispatchEvent(new CustomEvent('delete', {
                    detail: this._data,
                    bubbles: true,
                    composed: true
                }));
            } catch {
                toastService.show('Failed to delete asset. Please try again.');
            }
        };

    }
}

customElements.define('hat-asset-card', HatAssetCard);